import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  FolderPlus, Check, Trash2, Megaphone, Sparkles, TrendingUp, Plus, Pencil, CalendarClock,
} from "lucide-react";
import { toast } from "sonner";

const GOAL_LABELS = {
  build_audience: "Build audience", generate_leads: "Generate leads", sell_products: "Sell products",
  promote_services: "Promote services", promote_affiliate: "Affiliate", educate: "Educate",
};

export default function Projects() {
  const navigate = useNavigate();
  const { refreshProfile, refreshActiveProject } = useAuth();
  const [projects, setProjects] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [toDelete, setToDelete] = useState(null);

  const load = async () => {
    const { data } = await api.get("/projects");
    setProjects(data.projects || []);
    setActiveId(data.active_project_id);
    const camps = await api.get("/campaigns");
    setCampaigns(camps.data || []);
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const switchProject = async (id) => {
    if (id === activeId) return;
    await api.post(`/projects/${id}/activate`);
    await Promise.all([refreshProfile(), refreshActiveProject()]);
    toast.success("Switched project");
    setLoading(true);
    load();
  };

  const confirmDelete = async () => {
    const id = toDelete; setToDelete(null);
    await api.delete(`/projects/${id}`);
    toast.success("Project deleted");
    await Promise.all([refreshProfile(), refreshActiveProject()]);
    setLoading(true); load();
  };

  const removeCampaign = async (e, c) => {
    e.preventDefault(); e.stopPropagation();
    await api.delete(`/campaigns/${c.id}`);
    toast.success("Campaign deleted"); load();
  };

  const active = projects.find((p) => p.id === activeId);

  return (
    <div className="space-y-10" data-testid="projects-page">
      <div>
        <div className="flex items-end justify-between">
          <div>
            <p className="text-xs font-medium uppercase tracking-[0.2em] text-muted-foreground">Projects</p>
            <h1 className="font-head text-4xl font-bold">Your projects</h1>
            <p className="text-muted-foreground">Each project is one goal, with its own offer, campaigns &amp; Income Mode.</p>
          </div>
          <Button onClick={() => navigate("/projects/new")} data-testid="new-project-btn"
            className="rounded-full bg-primary px-6 py-6 text-base font-semibold hover:bg-primary/90">
            <FolderPlus size={18} className="mr-1.5" /> New project
          </Button>
        </div>

        {loading ? <p className="mt-6 text-muted-foreground">Loading…</p> : (
          <div className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
            {projects.map((p) => (
              <Card key={p.id} data-testid={`project-card-${p.id}`}
                className={`flex flex-col p-6 transition-transform duration-200 hover:-translate-y-1 hover:shadow-lg ${
                  p.is_active ? "border-primary ring-2 ring-primary/40" : "border-border"
                }`}>
                <div className="flex items-start justify-between gap-2">
                  <span className="inline-flex items-center gap-1 rounded-md bg-secondary px-2 py-0.5 text-xs font-medium text-secondary-foreground">
                    {GOAL_LABELS[p.goal] || p.goal}
                  </span>
                  {p.is_active && (
                    <span data-testid={`active-tag-${p.id}`} className="inline-flex items-center gap-1 rounded-md bg-primary/10 px-2 py-0.5 text-xs font-semibold text-primary">
                      <Check size={11} /> Active
                    </span>
                  )}
                </div>
                <h3 className="mt-2 font-head text-xl font-bold">{p.name}</h3>
                <p className="mt-1 line-clamp-1 text-sm text-muted-foreground">{p.offer?.selling || "Offer not set up yet"}</p>
                <div className="mt-2 flex items-center gap-3 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1"><Sparkles size={12} /> {p.campaign_count} campaigns</span>
                  {p.offer && !p.offer.pitch_test_done && (
                    <span className="flex items-center gap-1 text-amber-600"><Megaphone size={12} /> Pitch Test needed</span>
                  )}
                </div>
                <div className="mt-5 flex gap-2 pt-1">
                  {p.is_active ? (
                    <Button size="sm" disabled variant="outline" className="rounded-full">Active</Button>
                  ) : (
                    <Button size="sm" onClick={() => switchProject(p.id)} data-testid={`switch-project-${p.id}`}
                      className="rounded-full bg-primary hover:bg-primary/90">Switch to</Button>
                  )}
                  {p.offer && (
                    <Button size="sm" variant="ghost" onClick={() => navigate(`/offer-profiles/${p.offer.id}`)}
                      data-testid={`edit-offer-${p.id}`} className="rounded-full"><Pencil size={14} className="mr-1" /> Offer</Button>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => setToDelete(p.id)}
                    data-testid={`delete-project-${p.id}`} className="ml-auto rounded-full text-muted-foreground hover:text-destructive">
                    <Trash2 size={15} />
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {active && (
        <div>
          <div className="flex items-center justify-between">
            <h2 className="font-head text-2xl font-bold">
              Campaigns in <span className="text-primary">{active.name}</span>
            </h2>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => navigate("/income-mode")} className="rounded-full" data-testid="dash-income-btn">
                <TrendingUp size={16} className="mr-1" /> Income Mode
              </Button>
              <Button onClick={() => navigate("/campaigns/new")} className="rounded-full bg-primary hover:bg-primary/90" data-testid="dash-new-campaign-btn">
                <Plus size={16} className="mr-1" /> New campaign
              </Button>
            </div>
          </div>
          {campaigns.length === 0 ? (
            <Card className="mt-4 border-dashed border-border p-10 text-center text-muted-foreground">
              No campaigns yet in this project. Start one above.
            </Card>
          ) : (
            <div className="mt-4 grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {campaigns.map((c) => (
                <Card key={c.id} onClick={() => navigate(`/campaigns/${c.id}`)}
                  data-testid={`campaign-card-${c.id}`}
                  className="group relative cursor-pointer border-border p-5 transition-transform duration-200 hover:-translate-y-1 hover:shadow-lg">
                  <button onClick={(e) => removeCampaign(e, c)} data-testid={`delete-campaign-${c.id}`}
                    className="absolute right-3 top-3 rounded-lg p-1.5 text-muted-foreground opacity-0 transition-opacity hover:text-destructive group-hover:opacity-100">
                    <Trash2 size={15} />
                  </button>
                  <h3 className="font-head text-lg font-bold">{c.name || c.topic}</h3>
                  <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">{c.topic}</p>
                  <p className="mt-3 text-xs text-muted-foreground">
                    {(c.platforms || []).join(", ")} · {c.num_lessons} lessons
                  </p>
                  {c.created_at && (
                    <p className="mt-2 flex items-center gap-1.5 border-t border-border pt-2 text-xs text-muted-foreground"
                      data-testid={`campaign-created-${c.id}`}>
                      <CalendarClock size={12} />
                      Created {new Date(c.created_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })}
                    </p>
                  )}
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent data-testid="delete-project-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this project?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently removes the project, its offer, all its campaigns and Income Mode history. This can't be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-project">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} data-testid="confirm-delete-project"
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
